import { useEffect, useState } from 'react';
import { useAuth } from 'zite-auth-sdk';
import { getInfoHub, saveAnnouncement, acknowledgeAnnouncement, GetInfoHubOutputType } from 'zite-endpoints-sdk';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Megaphone, Search, Pin, Plus, Eye, CheckCircle } from 'lucide-react';
import { formatDistanceToNow, parseISO } from 'date-fns';

export default function InfoHub() {
  const { user } = useAuth();
  const [data, setData] = useState<GetInfoHubOutputType | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [addDialog, setAddDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState<any>(null);
  const [form, setForm] = useState({ title: '', body: '', audience: 'All Staff', category: 'General', noticeType: 'Announcement', acknowledgementType: 'None', status: 'Active', pinned: false, expiryDate: '' });

  useEffect(() => { if (user) load(); }, [user]);
  const load = () => { setLoading(true); getInfoHub({ search, category: catFilter || undefined }).then(setData).finally(() => setLoading(false)); };
  useEffect(() => { if (user) load(); }, [search, catFilter]);

  const isRead = (noticeId: string) => (data?.reads || []).some((r: any) => { const aid = Array.isArray(r.announcement) ? r.announcement[0] : r.announcement; return aid === noticeId && r.acknowledged; });

  const handleAcknowledge = async (notice: any) => {
    try {
      await acknowledgeAnnouncement({ announcementId: notice.id });
      toast.success('Acknowledged');
      load();
    } catch { toast.error('Failed'); }
  };

  const handleSave = async () => {
    if (!form.title) return;
    try {
      await saveAnnouncement({ ...form, pinned: form.pinned, expiryDate: form.expiryDate || undefined, noticeType: form.noticeType, category: form.category } as any);
      toast.success('Notice published');
      setAddDialog(false);
      load();
    } catch { toast.error('Failed'); }
  };

  const categories = ['General', 'Training', 'Policy', 'Event', 'FAQ', 'Urgent'];
  const catColor: Record<string, string> = { General: 'bg-blue-500/10 text-blue-700', Training: 'bg-green-500/10 text-green-700', Policy: 'bg-purple-500/10 text-purple-700', Event: 'bg-orange-500/10 text-orange-700', FAQ: 'bg-cyan-500/10 text-cyan-700', Urgent: 'bg-red-500/10 text-red-700' };

  if (loading) return <div className="p-6 space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>;

  const notices = data?.notices || [];
  const pinned = notices.filter((n: any) => n.pinned);
  const regular = notices.filter((n: any) => !n.pinned);

  return (
    <div className="p-4 md:p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Megaphone className="w-6 h-6 text-primary" /> Information Hub</h1>
          <p className="text-sm text-muted-foreground mt-1">Notices, messages, FAQs & announcements</p>
        </div>
        <div className="flex items-center gap-2">
          {data?.isManager && <Button size="sm" onClick={() => setAddDialog(true)}><Plus className="w-4 h-4 mr-1" /> New Notice</Button>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-9" />
        </div>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-36"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Pinned */}
      {pinned.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-sm font-semibold text-muted-foreground flex items-center gap-1"><Pin className="w-3.5 h-3.5" /> Pinned</h2>
          {pinned.map((n: any) => <NoticeCard key={n.id} notice={n} isRead={isRead(n.id)} onView={() => setViewDialog(n)} onAck={() => handleAcknowledge(n)} catColor={catColor} />)}
        </div>
      )}

      <div className="space-y-3">
        {regular.map((n: any) => <NoticeCard key={n.id} notice={n} isRead={isRead(n.id)} onView={() => setViewDialog(n)} onAck={() => handleAcknowledge(n)} catColor={catColor} />)}
        {notices.length === 0 && <p className="text-muted-foreground text-sm text-center py-8">No notices</p>}
      </div>

      {/* View Dialog */}
      <Dialog open={!!viewDialog} onOpenChange={() => setViewDialog(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{viewDialog?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2 flex-wrap">
              <Badge className={catColor[viewDialog?.category || 'General']}>{viewDialog?.category || 'General'}</Badge>
              {viewDialog?.noticeType && <Badge variant="outline">{viewDialog?.noticeType}</Badge>}
              <Badge variant="outline">{viewDialog?.audience}</Badge>
            </div>
            <div className="text-sm whitespace-pre-wrap">{viewDialog?.body}</div>
            {viewDialog?.createdAt && <p className="text-xs text-muted-foreground">Posted {formatDistanceToNow(parseISO(viewDialog.createdAt), { addSuffix: true })}</p>}
          </div>
          {viewDialog?.acknowledgementType !== 'None' && !isRead(viewDialog?.id || '') && (
            <DialogFooter>
              <Button onClick={() => { handleAcknowledge(viewDialog); setViewDialog(null); }}><CheckCircle className="w-4 h-4 mr-1" /> Acknowledge</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>New Notice</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} />
            <Textarea placeholder="Body" value={form.body} onChange={e => setForm({ ...form, body: e.target.value })} rows={4} />
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.audience} onValueChange={v => setForm({ ...form, audience: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['All Staff','Front of House','Back of House','Kitchen','Management'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.noticeType} onValueChange={v => setForm({ ...form, noticeType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['Announcement','Manager Message','FAQ','Policy Update','Event Info'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={form.acknowledgementType} onValueChange={v => setForm({ ...form, acknowledgementType: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['None','Tick to Confirm','Type Initials'].map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Switch checked={form.pinned} onCheckedChange={v => setForm({ ...form, pinned: v })} /><span className="text-sm">Pinned</span></div>
              <Input type="date" placeholder="Expiry" value={form.expiryDate} onChange={e => setForm({ ...form, expiryDate: e.target.value })} className="w-40" />
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddDialog(false)}>Cancel</Button><Button onClick={handleSave}>Publish</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function NoticeCard({ notice, isRead, onView, onAck, catColor }: { notice: any; isRead: boolean; onView: () => void; onAck: () => void; catColor: Record<string, string> }) {
  return (
    <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={onView}>
      <CardContent className="py-4 flex justify-between items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {notice.pinned && <Pin className="w-3.5 h-3.5 text-primary shrink-0" />}
            <h3 className="font-semibold">{notice.title}</h3>
            <Badge className={catColor[notice.category || 'General'] || 'bg-muted text-muted-foreground'} >{notice.category || 'General'}</Badge>
            {notice.noticeType && notice.noticeType !== 'Announcement' && <Badge variant="outline" className="text-xs">{notice.noticeType}</Badge>}
          </div>
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{notice.body}</p>
          <p className="text-xs text-muted-foreground mt-1">{notice.createdAt ? formatDistanceToNow(parseISO(notice.createdAt), { addSuffix: true }) : ''}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isRead ? <Badge className="bg-green-500/10 text-green-700 text-xs"><CheckCircle className="w-3 h-3 mr-1" />Read</Badge> : notice.acknowledgementType !== 'None' && <Badge variant="outline" className="text-xs">Needs Ack</Badge>}
        </div>
      </CardContent>
    </Card>
  );
}
